import { useEffect, useRef } from 'react';
import { useDAWStore } from '../store/daw-store';

interface PanelSizeConfig {
  panelId: string;
  currentSize: number;
  onResize: (newSize: number) => void;
}

/**
 * Hook for handling window resize and proportional panel scaling
 * Requirements: 8.5 - Scale panels proportionally, maintain aspect ratios, update layout state
 */
export function useWindowResize({ panelId, currentSize, onResize }: PanelSizeConfig) {
  const setPanelSize = useDAWStore((state) => state.setPanelSize);
  const currentLayout = useDAWStore((state) => state.currentLayout);
  const setLayout = useDAWStore((state) => state.setLayout);
  
  // Store the initial window size and panel size ratio
  const initialWindowSizeRef = useRef<number>(0);
  const initialPanelSizeRef = useRef<number>(currentSize);
  const panelRatioRef = useRef<number>(0);

  useEffect(() => {
    // Initialize on mount
    initialWindowSizeRef.current = window.innerWidth;
    initialPanelSizeRef.current = currentSize;
    panelRatioRef.current = currentSize / window.innerWidth;

    const handleResize = () => {
      const newWindowWidth = window.innerWidth;
      const windowSizeChange = newWindowWidth / initialWindowSizeRef.current;
      
      // Calculate new panel size proportionally (Requirements 8.5)
      const newPanelSize = Math.round(initialPanelSizeRef.current * windowSizeChange);
      
      // Update panel size
      onResize(newPanelSize);
      
      // Update the stored panel size in the store
      setPanelSize(panelId, newPanelSize);
      
      // Update the current layout with new panel sizes (Requirements 8.5)
      const updatedLayout = {
        ...currentLayout,
        savedPanelSizes: {
          ...currentLayout.savedPanelSizes,
          [panelId]: newPanelSize,
        },
      };
      
      // This will persist to localStorage and sync to backend
      setLayout(updatedLayout);
      
      // Update the initial values for next resize
      initialWindowSizeRef.current = newWindowWidth;
      initialPanelSizeRef.current = newPanelSize;
    };

    // Debounce the resize handler to avoid excessive updates
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const debouncedHandleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      clearTimeout(resizeTimeout);
    };
  }, [panelId, currentSize, onResize, setPanelSize, currentLayout, setLayout]);
}

/**
 * Hook for handling window resize for height-based panels (mixer, channel rack)
 */
export function useWindowResizeHeight({ panelId, currentSize, onResize }: PanelSizeConfig) {
  const setPanelSize = useDAWStore((state) => state.setPanelSize);
  const currentLayout = useDAWStore((state) => state.currentLayout);
  const setLayout = useDAWStore((state) => state.setLayout);
  
  const initialWindowHeightRef = useRef<number>(0);
  const initialPanelSizeRef = useRef<number>(currentSize);

  useEffect(() => {
    // Initialize on mount
    initialWindowHeightRef.current = window.innerHeight;
    initialPanelSizeRef.current = currentSize;

    const handleResize = () => {
      const newWindowHeight = window.innerHeight;
      const windowSizeChange = newWindowHeight / initialWindowHeightRef.current;
      
      // Calculate new panel size proportionally (Requirements 8.5)
      const newPanelSize = Math.round(initialPanelSizeRef.current * windowSizeChange);
      
      // Update panel size
      onResize(newPanelSize);
      
      // Update the stored panel size in the store
      setPanelSize(panelId, newPanelSize);
      
      // Update the current layout with new panel sizes (Requirements 8.5)
      const updatedLayout = {
        ...currentLayout,
        savedPanelSizes: {
          ...currentLayout.savedPanelSizes,
          [panelId]: newPanelSize,
        },
      };
      
      // This will persist to localStorage and sync to backend
      setLayout(updatedLayout);
      
      // Update the initial values for next resize
      initialWindowHeightRef.current = newWindowHeight;
      initialPanelSizeRef.current = newPanelSize;
    };

    // Debounce the resize handler to avoid excessive updates
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const debouncedHandleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      clearTimeout(resizeTimeout);
    };
  }, [panelId, currentSize, onResize, setPanelSize, currentLayout, setLayout]);
}
